/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    // On close, remove every "Just added to cart" hidden prefix from cart
    // items so the next time the drawer opens VoiceOver no longer reads them
    // as part of the items' accessible names. Also clear the live regions to
    // prevent any residual "X eliminado" / "Cantidad actualizada" replays.
    this.addEventListener('on:cart-drawer:after-close', () => {
      this.querySelectorAll('.cart-item__just-added').forEach((el) => el.remove());
      const drawerRegion = document.getElementById('cart-drawer-live-region');
      if (drawerRegion) drawerRegion.textContent = '';
      const globalRegion = document.getElementById('cart-add-live-region');
      if (globalRegion) globalRegion.textContent = '';
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    const detail = evt.detail || {};
    this.open(detail.opener || null, detail.elementToFocus || undefined);
  }

  /**
   * Override of SideDrawer.open: pick a sensible focus target based on the
   * cart state.
   *
   *  - Empty cart: focus the empty-state container (it ships with
   *    tabindex="-1" and stays visible while .cart-drawer__content is hidden,
   *    so the Escape listener bound to the drawer can still receive keys).
   *
   *  - Non-empty cart: focus the first cart item's title link, so VoiceOver
   *    announces the dialog name + the product title (giving the user
   *    immediate context about what's in the cart) instead of just "Carrito".
   *    The link is a leaf focusable, which avoids VoiceOver's "and N more
   *    items" recap that fires when focus lands on a large container.
   *
   *  - Fallbacks: the cart title (#CartDrawerModal-Title, tabindex="-1") and
   *    finally the drawer element itself, so we never end up with focus on
   *    <body> (which would bypass the Escape listener).
   */
  open(opener, elementToFocus, callback) {
    // If the drawer is already open (e.g. user adds another item from the
    // cross-sell carousel inside the open drawer), do NOT re-run super.open():
    // it would re-trap focus on the title, which makes VoiceOver re-read
    // "Carrito" and interrupt the live-region announcement. Just no-op so the
    // pending live-region message can be heard cleanly.
    if (this.hasAttribute('open')) {
      if (typeof callback === 'function') callback();
      return;
    }

    let target = elementToFocus;
    if (!target) {
      // Detect empty cart by class state, not by offsetParent/visibility:
      // the drawer is still visibility:hidden when open() is invoked (super
      // sets [open] right after this), so live visibility checks lie.
      const content = this.querySelector('.cart-drawer__content');
      const isEmpty = content && content.classList.contains('cart-drawer__content--hidden');
      if (isEmpty) {
        target = this.querySelector('.cart-drawer__empty-content');
      } else {
        target = this.querySelector('[data-cart-item-title-link]')
          || this.querySelector('#CartDrawerModal-Title');
      }
      if (!target) target = this;
    }
    super.open(opener, target, callback);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

window.customElements.define('cart-drawer', CartDrawer);
